import React, { useState, useMemo, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { observer } from 'mobx-react';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Box,
  IconButton,
} from '@mui/material';
import Link from '@mui/material/Link';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { TableVirtuoso } from 'react-virtuoso';
import tableOrWineNumberOptions from 'constants/table-or-wine-number-options';
import wineListsStore from 'store/wineListsStore';
import { routes } from 'config/index';
import M from 'messages';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CancelIcon from '@mui/icons-material/Cancel';
import classes from '../styles';

// Define columns configuration
const columns = [
  { width: '24px', label: '', dataKey: 'drag' },
  { width: '14%', label: 'table.wineListWines.producer', dataKey: 'producer' },
  { width: '14%', label: 'table.wineListWines.name', dataKey: 'name' },
  { width: '8%', label: 'table.wineListWines.wineCountry', dataKey: 'wineCountry' },
  { width: '8%', label: 'table.wineListWines.region', dataKey: 'region' },
  { width: '6%', label: 'table.wineListWines.year', dataKey: 'year' },
  { width: '6%', label: 'table.wineListWines.color', dataKey: 'color' },
  { width: '7%', label: 'table.wineListWines.style', dataKey: 'style' },
  { width: '7%', label: 'table.wineListWines.sweetness', dataKey: 'sweetness' },
  { width: '6%', label: 'table.wineListWines.isSuperVino', dataKey: 'isSuperVino' },
  { width: '10%', label: 'table.wineListWines.isTableOrWine', dataKey: 'isTableOrWine' },
  { width: '8%', label: 'table.wineListWines.tableNumber', dataKey: 'tableOrWineNumber' },
  { width: '4%', label: '', dataKey: 'remove' },
];

// Simple cell content component (no drag functionality)
const SimpleCellContent = ({ children }) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}>
      {children}
    </Box>
  );
};

SimpleCellContent.propTypes = {
  children: PropTypes.node.isRequired,
};

// Memoized cell content components
const TableOrWineSelect = React.memo(({ item, disabled }) => (
  <FormControl fullWidth size="small" variant="standard">
    <Select
      id={`is-table-or-wine-${item.id}`}
      name="isTableOrWine"
      disabled={disabled}
      value={item.isTableOrWine || ''}
      onChange={e => wineListsStore.handleChangeIsTableOrWine(item.id, e)}
      sx={{
        fontSize: '0.875rem',
        fontFamily: 'unset',
      }}>
      <MenuItem
        sx={{
          fontSize: '0.875rem',
          fontFamily: 'unset',
        }}
        value="">
        <em>{M.get('menuItem.tableOrWineNumberOptions.notSelected')}</em>
      </MenuItem>
      {tableOrWineNumberOptions.map(option => (
        <MenuItem
          key={option.value}
          value={option.value}
          sx={{
            fontSize: '0.875rem',
            fontFamily: 'unset',
          }}>
          {option.title}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
));

const TableOrWineNumberInput = React.memo(({ item, disabled }) => {
  const [localValue, setLocalValue] = React.useState(item.tableOrWineNumber || '');

  // Update local value when item changes
  React.useEffect(() => {
    setLocalValue(item.tableOrWineNumber || '');
  }, [item.tableOrWineNumber]);

  const handleChange = React.useCallback(
    e => {
      const value = e.target.value;
      setLocalValue(value);
      wineListsStore.handleChangeIsTableOrWine(item.id, e);
    },
    [item.id],
  );

  return (
    <FormControl>
      <TextField
        size="small"
        variant="standard"
        id={`tableOrWineNumber-${item.id}`}
        name="tableOrWineNumber"
        disabled={disabled}
        value={localValue}
        onChange={handleChange}
        type="text"
        slotProps={{
          htmlInput: { maxLength: 14 },
          input: {
            style: {
              fontSize: '0.875rem',
              fontFamily: 'unset',
            },
          },
        }}
      />
    </FormControl>
  );
});

const TableOrWineRemoveButton = React.memo(({ item, disabled }) => {
  const handleClick = React.useCallback(
    e => {
      e.stopPropagation();
      const remaining = wineListsStore.getSelectedWines.filter(wine => String(wine.id) !== String(item.id));
      wineListsStore.onChangeItemsField(null, remaining, 'removeOption');
    },
    [item.id],
  );

  return (
    <FormControl>
      <IconButton
        aria-label="Remove wine"
        size="small"
        disabled={disabled}
        onClick={handleClick}
        onPointerDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}>
        <CancelIcon fontSize="small" />
      </IconButton>
    </FormControl>
  );
});

TableOrWineSelect.propTypes = {
  item: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

TableOrWineNumberInput.propTypes = {
  item: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

TableOrWineRemoveButton.propTypes = {
  item: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

// Render cell content based on column dataKey
const renderCellContent = (dataKey, item, disabled) => {
  switch (dataKey) {
    case 'drag':
      return (
        <SimpleCellContent>
          <DragIndicatorIcon sx={classes.dragIcon} />
        </SimpleCellContent>
      );
    case 'name': {
      const winePath = generatePath(routes.wineEdit.path, { id: item.id });
      return (
        <SimpleCellContent>
          <Link component={RouterLink} to={winePath} underline="hover" onClick={e => e.stopPropagation()}>
            {item.name}
          </Link>
        </SimpleCellContent>
      );
    }
    case 'producer':
      return <SimpleCellContent>{item.producer}</SimpleCellContent>;
    case 'wineCountry':
      return <SimpleCellContent>{item.wineCountry}</SimpleCellContent>;
    case 'region':
      return <SimpleCellContent>{item.region}</SimpleCellContent>;
    case 'year':
      return <SimpleCellContent>{item.year}</SimpleCellContent>;
    case 'color':
      return <SimpleCellContent>{item.color}</SimpleCellContent>;
    case 'style':
      return <SimpleCellContent>{item.style}</SimpleCellContent>;
    case 'sweetness':
      return <SimpleCellContent>{item.sweetness}</SimpleCellContent>;
    case 'isSuperVino':
      return <SimpleCellContent>{item.isSuperVino ? 'Yes' : 'No'}</SimpleCellContent>;
    case 'isTableOrWine':
      return <TableOrWineSelect item={item} disabled={disabled} />;
    case 'tableOrWineNumber':
      return <TableOrWineNumberInput item={item} disabled={disabled} />;
    case 'remove':
      return <TableOrWineRemoveButton item={item} />;
    default:
      return null;
  }
};

// Wrapper component to pass disabled prop
const TableRowWrapper = observer(({ item, disabled }) => {
  return <DraggableTableRow item={item} disabled={disabled} />;
});

// Draggable TableRow component
const DraggableTableRow = observer(({ item, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(item.order),
  });

  const rowStyle = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    background: isDragging ? '#f0f0f0' : !item.active ? 'rgb(227 227 227)' : 'white',
    cursor: 'grab',
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={rowStyle}
      sx={{
        '&:active': {
          cursor: 'grabbing',
        },
      }}>
      {columns.map(column => {
        const isFormControl = column.dataKey === 'isTableOrWine' || column.dataKey === 'tableOrWineNumber';
        return (
          <TableCell
            key={column.dataKey}
            align="left"
            {...(!isFormControl ? { ...listeners, ...attributes } : {})}
            sx={{
              ...classes.menuWinesTableCell,
              ...(isFormControl && {
                cursor: 'default',
                paddingTop: '0',
                paddingBottom: '0',
              }),
            }}>
            {renderCellContent(column.dataKey, item, disabled)}
          </TableCell>
        );
      })}
    </TableRow>
  );
});

DraggableTableRow.propTypes = {
  item: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

// VirtuosoTableComponents for Material-UI integration
const createVirtuosoTableComponents = disabled => ({
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer
      component={Paper}
      {...props}
      ref={ref}
      sx={{
        maxHeight: '100%',
        overflowX: 'auto',
      }}
    />
  )),
  Table: props => (
    <Table
      {...props}
      sx={{
        minWidth: '920px', // Ensure minimum width for horizontal scroll
      }}
    />
  ),
  TableHead: React.forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow: props => <TableRowWrapper {...props} disabled={disabled} />,
  TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
});

const DraggableTable = observer(({ disabled }) => {
  const [sortConfig, setSortConfig] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor));

  // Get items directly from store - no local state needed
  const displayedItems = wineListsStore.getSelectedWines;

  // Memoize the table components to prevent re-creation on every render
  const tableComponents = useMemo(() => createVirtuosoTableComponents(disabled), [disabled]);

  const handleSortRequest = useCallback(
    field => {
      const direction = sortConfig?.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
      setSortConfig({ field, direction });

      const sortedItems = [...displayedItems].sort((a, b) => {
        if (a[field] < b[field]) return direction === 'asc' ? -1 : 1;
        if (a[field] > b[field]) return direction === 'asc' ? 1 : -1;
        return 0;
      });

      wineListsStore.setSortedSelectedWines(sortedItems); // Update store
    },
    [sortConfig, displayedItems],
  );

  // Fixed header content for the virtualized table
  const fixedHeaderContent = useCallback(() => {
    return (
      <TableRow>
        {columns.map(column => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align="left"
            style={{ width: column.width }}
            sx={{ backgroundColor: 'background.paper' }}>
            {column.label && (
              <TableSortLabel
                active={sortConfig?.field === column.dataKey}
                direction={sortConfig?.direction || 'asc'}
                onClick={() => handleSortRequest(column.dataKey)}
                disabled={disabled}>
                {M.get(column.label)}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    );
  }, [sortConfig, disabled, handleSortRequest]);

  // Row content for the virtualized table
  const rowContent = useCallback((_index, item) => {
    return item;
  }, []);

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = displayedItems.findIndex(item => String(item.order) === active.id);
    const newIndex = displayedItems.findIndex(item => String(item.order) === over.id);
    const newItems = arrayMove(displayedItems, oldIndex, newIndex);

    wineListsStore.setSortedSelectedWines(newItems); // Update store
  };

  return (
    !!displayedItems.length && (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={displayedItems.map(item => String(item.order))} strategy={verticalListSortingStrategy}>
          <Paper
            style={{
              height: displayedItems.length <= 25 ? `${58 + Math.ceil(displayedItems.length / 5) * 350}px` : '1500px',
              width: '100%',
            }}>
            <TableVirtuoso
              data={displayedItems}
              components={tableComponents}
              fixedHeaderContent={fixedHeaderContent}
              itemContent={rowContent}
              fixedItemHeight={60}
              overscan={10}
              increaseViewportBy={200}
            />
          </Paper>
        </SortableContext>
      </DndContext>
    )
  );
});

DraggableTable.propTypes = {
  disabled: PropTypes.bool,
};

export default DraggableTable;
